import { useState } from 'react';
import { Mail, User, Phone, Ruler, Scale, MapPin, CheckCircle } from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { format, parse, isValid } from 'date-fns';
import { calculateAge } from '@/lib/dateUtils';

interface FormDataFields {
  fullName: string;
  email: string;
  phone: string;
  state: string;
  age: number | null;
  dateOfBirth: string;
  heightFeet: number | null;
  heightInches: number | null;
  weight: number | null;
  smsConsent: boolean;
}

interface PersonalInfoStepProps {
  formData: FormDataFields;
  updateFormData: (field: string, value: string | number | boolean | null) => void;
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function PersonalInfoStep({ formData, updateFormData }: PersonalInfoStepProps) {
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (email: string): string | null => {
    if (!email?.trim()) {
      return 'Email address is required';
    }
    if (!EMAIL_REGEX.test(email)) {
      return 'Please enter a valid email (e.g., name@example.com)';
    }
    return null;
  };

  const handleEmailChange = (email: string) => {
    updateFormData('email', email);
    if (emailTouched) {
      setEmailError(validateEmail(email));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(formData.email));
  };

  const calculateAge = (dob: string) => {
    if (!dob) return null;
    return calculateAge(dob);
  };

  const handleDateOfBirthChange = (date: Date | undefined) => {
    if (date && isValid(date)) {
      const isoDate = format(date, 'yyyy-MM-dd');
      updateFormData('dateOfBirth', isoDate);
      const age = calculateAge(isoDate);
      if (age !== null) {
        updateFormData('age', age);
      }
    } else {
      updateFormData('dateOfBirth', '');
      updateFormData('age', null);
    }
  };

  // Parse stored date for DatePicker
  const dateValue = formData.dateOfBirth ? (() => {
    const parsedDate = parse(formData.dateOfBirth, 'yyyy-MM-dd', new Date());
    return isValid(parsedDate) ? parsedDate : undefined;
  })() : undefined;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#2FFFC7' }}>
          Step 2 of 7
        </p>
        <h2 className="text-3xl font-light tracking-tight" style={{ color: '#E6E9F0' }}>
          Personal Information
        </h2>
        <p className="text-lg" style={{ color: '#C1C9D2' }}>
          Help us get to know you better
        </p>
      </div>

      {/* Contact Information */}
      <div>
        <h3 className="text-xl font-semibold mb-6" style={{ color: '#2FFFC7' }}>
          Contact Information
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
              Full Name <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <div className="relative">
              <User 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: '#C1C9D2' }}
              />
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => updateFormData('fullName', e.target.value)}
                placeholder="Enter your full name"
                required
                className="w-full pl-10 pr-4 py-3 rounded-lg text-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(47, 255, 199, 0.2)',
                  color: '#E6E9F0',
                }}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
              Email Address <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <div className="relative">
              <Mail 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: '#C1C9D2' }}
              />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                placeholder="your@email.com"
                required
                className="w-full pl-10 pr-4 py-3 rounded-lg text-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: emailError && emailTouched ? '1px solid #EF4444' : '1px solid rgba(47, 255, 199, 0.2)',
                  color: '#E6E9F0',
                }}
              />
            </div>
            {emailError && emailTouched && (
              <p className="mt-1 text-xs text-red-400">{emailError}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
              Phone Number
            </label>
            <div className="relative">
              <Phone 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: '#C1C9D2' }}
              />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', e.target.value)}
                placeholder="(555) 555-5555"
                className="w-full pl-10 pr-4 py-3 rounded-lg text-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(47, 255, 199, 0.2)',
                  color: '#E6E9F0',
                }}
              />
            </div>

            {/* SMS Consent */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => updateFormData('smsConsent', !formData.smsConsent)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') updateFormData('smsConsent', !formData.smsConsent) }}
              className="flex items-start gap-3 cursor-pointer mt-3"
            >
              <div
                className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
                style={{
                  background: formData.smsConsent ? '#2FFFC7' : 'rgba(255, 255, 255, 0.05)',
                  border: formData.smsConsent ? 'none' : '1px solid rgba(47, 255, 199, 0.2)',
                }}
              >
                {formData.smsConsent && <CheckCircle className="w-3.5 h-3.5" style={{ color: '#000' }} />}
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium" style={{ color: '#E6E9F0' }}>
                  I agree to receive text messages from Nimbus Providers
                </span>
                <p className="text-xs leading-relaxed" style={{ color: '#C1C9D2' }}>
                  Including prescription updates, refill reminders, and order status notifications.
                  Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to cancel. Reply HELP for help.
                </p>
              </div>
            </div>
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
              State <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <div className="relative">
              <MapPin 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
                style={{ color: '#C1C9D2' }}
              />
              <select
                value={formData.state}
                onChange={(e) => updateFormData('state', e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 rounded-lg text-sm appearance-none cursor-pointer"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(47, 255, 199, 0.2)',
                  color: formData.state ? '#E6E9F0' : '#6B7280',
                }}
              >
                <option value="" style={{ background: '#1a2332', color: '#C1C9D2' }}>Select your state</option>
                {US_STATES.map(state => (
                  <option 
                    key={state} 
                    value={state}
                    style={{ background: '#1a2332', color: '#E6E9F0' }}
                  >
                    {state}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Measurements */}
      <div>
        <h3 className="text-xl font-semibold mb-6" style={{ color: '#2FFFC7' }}>
          Personal Information
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
              Date of Birth <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <DatePicker
              value={dateValue}
              onValueChange={handleDateOfBirthChange}
              placeholder="MM/DD/YYYY"
              maxDate={new Date()}
              className="w-full rounded-lg"
              inputStyle={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(47, 255, 199, 0.2)',
                color: '#E6E9F0',
              }}
            />
            {formData.age !== null && (
              <p className="mt-2 text-xs" style={{ color: '#C1C9D2' }}>
                Age: {formData.age} years
              </p>
            )}
          </div>

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
              Weight (lbs)
            </label>
            <div className="relative">
              <Scale 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: '#C1C9D2' }}
              />
              <input
                type="number"
                value={formData.weight || ''}
                onChange={(e) => updateFormData('weight', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Enter weight in pounds"
                min="50"
                max="500"
                className="w-full pl-10 pr-4 py-3 rounded-lg text-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(47, 255, 199, 0.2)',
                  color: '#E6E9F0',
                }}
              />
            </div>
          </div>

          {/* Height */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
              Height
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <Ruler 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  style={{ color: '#C1C9D2' }}
                />
                <input
                  type="number"
                  value={formData.heightFeet || ''}
                  onChange={(e) => updateFormData('heightFeet', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Feet"
                  min="3"
                  max="8"
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-sm"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(47, 255, 199, 0.2)',
                    color: '#E6E9F0',
                  }}
                />
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={formData.heightInches || ''}
                  onChange={(e) => updateFormData('heightInches', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Inches"
                  min="0"
                  max="11"
                  className="w-full px-4 py-3 rounded-lg text-sm"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(47, 255, 199, 0.2)',
                    color: '#E6E9F0',
                  }}
                />
              </div>
            </div>
            {formData.heightFeet !== null && formData.heightInches !== null && (
              <p className="mt-2 text-xs" style={{ color: '#C1C9D2' }}>
                Total: {formData.heightFeet}'{formData.heightInches}" ({Math.round((formData.heightFeet * 12 + formData.heightInches) * 2.54)} cm)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Privacy Notice */}
      <div 
        className="p-4 rounded-lg"
        style={{
          background: 'rgba(47, 255, 199, 0.05)',
          border: '1px solid rgba(47, 255, 199, 0.2)',
        }}
      >
        <p className="text-xs leading-relaxed" style={{ color: '#C1C9D2' }}>
          <strong style={{ color: '#2FFFC7' }}>Privacy Note:</strong> Your personal information is securely stored and only shared with your assigned healthcare provider. We comply with HIPAA regulations and will never sell your data.
        </p>
      </div>
    </div>
  );
}
